import {
  validateDestinationRegions,
  validateSourceRegions,
} from '@/configuration/pages/ConfigurationPage/LogisticsConfiguration/helpers'

import {
  errorTypes,
  shippingMethodDescriptionMaxLength,
  shippingMethodDescriptionMaxStoredLength,
  shippingMethodDescriptionVariables,
} from './constants'

const descriptionTokenRegex = /(\{\{[a-z_]+\}\})/

const getDescriptionVariableByToken = token => {
  return shippingMethodDescriptionVariables.find(descriptionVariable => descriptionVariable.variable === token)
}

const addDescriptionSegment = (segments, segment) => {
  const descriptionVariable = getDescriptionVariableByToken(segment)
  const lastSegment = segments.at(-1)

  if (descriptionVariable) return [...segments, { variable: descriptionVariable }]
  if (lastSegment?.text) return [...segments.slice(0, -1), { text: lastSegment.text + segment }]

  return [...segments, { text: segment }]
}

export const splitDescriptionIntoSegments = description => {
  return description
    .split(descriptionTokenRegex)
    .filter(segment => segment !== '')
    .reduce(addDescriptionSegment, [])
}

export const getDescriptionRenderedLength = description => {
  return splitDescriptionIntoSegments(description).reduce((length, segment) => {
    if (segment.variable) return length + segment.variable.exampleValue.length

    return length + segment.text.length
  }, 0)
}

const validateBasicInfo = shippingMethod => {
  let errors = []

  if (shippingMethod?.internal_name === '') {
    errors.push({
      type: errorTypes.nameOrInternalNameEmpty,
    })
    errors.push({
      type: errorTypes.internalNameEmpty,
    })
  }

  if (shippingMethod?.name === '') {
    errors.push({
      type: errorTypes.nameOrInternalNameEmpty,
    })
    errors.push({
      type: errorTypes.nameEmpty,
    })
  }

  if (shippingMethod?.description === '') {
    errors.push({
      type: errorTypes.descriptionEmpty,
    })
  }

  if (
    getDescriptionRenderedLength(shippingMethod.description) > shippingMethodDescriptionMaxLength ||
    shippingMethod.description.length > shippingMethodDescriptionMaxStoredLength
  ) {
    errors.push({
      type: errorTypes.descriptionTooLong,
    })
  }

  return errors
}

const validateLogistic = (shippingMethod, regions) => {
  let errors = []

  if (!shippingMethod?.carrier?.id) {
    errors.push({
      type: errorTypes.carrierEmpty,
    })
  }

  if (
    !validateSourceRegions({
      sourceRegionIds: shippingMethod?.carrier?.source_region_ids,
      methodRegions: shippingMethod?.regions,
      allRegions: regions,
    })
  ) {
    errors.push({
      type: errorTypes.invalidRegionForCarrierService,
    })
  }

  if (
    !validateDestinationRegions({
      destinationRegionIds: shippingMethod?.carrier?.destination_region_ids,
      location: shippingMethod?.location,
      allRegions: regions,
    })
  ) {
    errors.push({
      type: errorTypes.invalidLocationForCarrierService,
    })
  }

  return errors
}

const validateSourceAddress = shippingMethod => {
  let errors = []

  if (!shippingMethod?.location?.id) {
    errors.push({
      type: errorTypes.locationIdEmpty,
    })
  }

  return errors
}

const validateRegions = shippingMethod => {
  let errors = []

  if (!shippingMethod?.regions?.length) {
    errors.push({
      type: errorTypes.regionsEmpty,
    })
  }

  return errors
}

const validateCurrency = shippingMethod => {
  let errors = []

  if (!shippingMethod?.currency) {
    errors.push({
      type: errorTypes.priceOrCurrencyEmpty,
    })
    errors.push({
      type: errorTypes.currencyEmpty,
    })
  }

  return errors
}

const validatePrice = shippingMethod => {
  let errors = []

  if (!shippingMethod?.price) {
    errors.push({
      type: errorTypes.priceOrCurrencyEmpty,
    })
    errors.push({
      type: errorTypes.priceEmpty,
    })
  }

  return errors
}

export const validateShippingMethod = (shippingMethod, regions) => {
  return [
    ...validateBasicInfo(shippingMethod),
    ...validateLogistic(shippingMethod, regions),
    ...validateSourceAddress(shippingMethod),
    ...validateRegions(shippingMethod),
    ...validateCurrency(shippingMethod),
    ...validatePrice(shippingMethod),
  ]
}

export const getDutiesAdjustmentRate = rate => {
  return rate ? parseFloat((parseFloat(rate) * 100).toFixed(2)) : 0
}

export const getDutiesAdjustmentRateFormatted = rate => {
  return parseFloat(((parseFloat(rate) || 0) / 100).toFixed(4))
}

export const getShippingMethodPayload = shippingMethod => {
  const newSm = JSON.parse(JSON.stringify({ ...shippingMethod }))

  newSm.carrier_service_id = newSm.carrier.id
  newSm.location_id = newSm.location.id
  newSm.regions = newSm.regions.map(r => r.id)
  newSm.delivery_days_range = {
    lower: parseInt(newSm?.delivery_days_range?.lower),
    upper: parseInt(newSm?.delivery_days_range?.upper),
  }

  newSm.duties_adjustment_rate = getDutiesAdjustmentRateFormatted(newSm.duties_adjustment_rate)

  delete newSm.carrier
  delete newSm.location
  delete newSm.availability_criterion_id

  return newSm
}
